import { Button, HStack, Text } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  ExpenseBudgetItemType,
  type ReopenExpenseBudgetItemMutation,
  useReopenExpenseBudgetItemMutation,
} from "@src/__generated__/graphql";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { observer } from "mobx-react-lite";
import React from "react";
import type { ExpenseItem } from "./models";
import StatItem from "./StatItem";

export interface CompletedSectionProps {
  expenseItem: ExpenseItem;
  onReopen: (
    data: ReopenExpenseBudgetItemMutation["reopenExpenseBudgetItem"],
  ) => void;
}

const CompletedSection = ({ expenseItem, onReopen }: CompletedSectionProps) => {
  const [reopen, { loading }] = useReopenExpenseBudgetItemMutation({
    variables: { id: expenseItem.id },
    onCompleted(data) {
      if (data?.reopenExpenseBudgetItem) {
        onReopen(data.reopenExpenseBudgetItem);
      }
    },
    ...onError(),
  });

  const canReopen = can([
    "project_budget_reopenExpenseBudgetItem_all",
    "project_budget_reopenExpenseBudgetItem_team",
    "project_budget_reopenExpenseBudgetItem_own",
  ]);

  return (
    <React.Fragment>
      <HStack justify="space-between" spacing="3">
        <StatItem
          label={t`Commission`}
          value={expenseItem.stats.planned_commission}
          percent={expenseItem.stats.planned_commission_relative_computed}
        />
        <StatItem
          label={t`Final commission`}
          value={expenseItem.stats.real_commission}
          percent={expenseItem.stats.real_commission_relative}
        />
        <StatItem
          label={t`Difference`}
          value={Math.abs(
            expenseItem.stats.real_commission -
              expenseItem.stats.planned_commission,
          )}
          percent={Math.abs(
            +expenseItem.stats.real_commission_relative -
              +expenseItem.stats.planned_commission_relative_computed,
          )}
        />
      </HStack>

      {canReopen && expenseItem.type !== ExpenseBudgetItemType.Other && (
        <HStack justify="space-between" mt="10">
          <Text>
            <Trans>Do you want to upload another expense?</Trans>
          </Text>
          <Button
            colorScheme="grey"
            isLoading={loading}
            onClick={() => reopen()}
            variant="outline"
          >
            <Trans>Open and add expense</Trans>
          </Button>
        </HStack>
      )}
    </React.Fragment>
  );
};

export default observer(CompletedSection);
